import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ApiError, bootstrap, changePassword, fetchMe, login, logout } from "./api";
import { queryClient } from "./query-client";

export const meQueryOptions = queryOptions({
  queryKey: ["me"] as const,
  queryFn: fetchMe,
  staleTime: 30_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQueryOptions);
}

export function useLogin() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      identifier,
      password,
      turnstileToken,
    }: {
      identifier: string;
      password: string;
      turnstileToken?: string | null;
    }) => login(identifier, password, turnstileToken),
    onSuccess: (me) => {
      client.setQueryData(meQueryOptions.queryKey, me);
      client.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useSetup() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: bootstrap,
    onSuccess: (me) => {
      client.setQueryData(meQueryOptions.queryKey, me);
    },
  });
}

export function useChangePassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => changePassword(currentPassword, newPassword),
    onSuccess: (me) => {
      client.setQueryData(meQueryOptions.queryKey, me);
    },
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      client.setQueryData(meQueryOptions.queryKey, null);
      client.clear();
    },
  });
}

export function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as ApiError).message);
  }
  return "操作失败，请稍后重试";
}

export { queryClient };
